import type { ReactElement, ReactNode } from 'react'
import { Save, Undo2 } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Tooltip } from '@highlight/ui'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'

interface FloatingSaveControlsProps {
  onSave: () => void
  onDiscard: () => void
  isBusy: boolean
  className?: string
  /** The CTA's text. Defaults to "Save changes". */
  saveLabel?: string
  /** Shown on the CTA straight away, for saves whose effect reaches beyond this app. */
  saveTooltip?: string
  /** Stacked above the buttons, e.g. a save-failure banner. */
  children?: ReactNode
}

/**
 * Dirty-state footer for floating chrome: a discard circle next to the "Save
 * changes" CTA. Presentational only; the host owns the draft and the ⌘↵ hotkey.
 */
export function FloatingSaveControls({
  onSave,
  onDiscard,
  isBusy,
  className,
  saveLabel = 'Save changes',
  saveTooltip,
  children,
}: FloatingSaveControlsProps): ReactElement {
  const saveButton = (
    <Button
      onClick={onSave}
      variant="primary"
      tone="success"
      className="shrink-0"
      disabled={isBusy}
      loading={isBusy}
      left={<Save aria-hidden="true" className="size-4" />}
      hotkey="cmd + enter"
    >
      {saveLabel}
    </Button>
  )
  return (
    <div className={twMerge('flex min-w-0 flex-col items-center gap-2', className)}>
      {children}
      <div className="flex shrink-0 items-center justify-center gap-2">
        <Button
          variant="outline"
          shape="square"
          aria-label="Discard changes"
          onClick={onDiscard}
          disabled={isBusy}
          className={FLOATING_FILL_CLASS}
        >
          <Undo2 aria-hidden="true" />
        </Button>
        {saveTooltip ? (
          <Tooltip>
            {/* No delay: the hint warns about a public save, so it must show before the click. */}
            <Tooltip.Trigger delay={0} render={saveButton} />
            <Tooltip.Content>{saveTooltip}</Tooltip.Content>
          </Tooltip>
        ) : (
          saveButton
        )}
      </div>
    </div>
  )
}
